// pages/salesReturns/AddSaleReturnPage.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { DatePicker } from 'antd';
import {
    ArrowLeft,
    Save,
    X,
    Search,
    RotateCcw
} from 'react-feather';
import Select from 'react-select';
import dayjs from 'dayjs';
import Swal from 'sweetalert2';
import { saleReturnActions } from '../../core/action/saleReturnActions';
import { customerActions } from '../../core/action/customerActions';

const AddSaleReturnPage = () => {
    const navigate = useNavigate();
    const searchInvoiceRef = useRef(null);

    // Rounding function
    const roundAmount = (amount) => {
        return Math.round(amount);
    };

    // Initial state
    const [formData, setFormData] = useState({
        reference_no: `SRN${dayjs().format('YYMMDD')}${Math.floor(Math.random() * 1000)}`,
        return_date: dayjs().format('YYYY-MM-DD'),
        sale_id: '',
        original_invoice_no: '',
        customer_id: '',
        customer_name: '',
        customer_phone: '',
        no_of_items: 0,
        total_qty: 0,
        subtotal: 0,
        tax: 0,
        discount: 0,
        shipping: 0,
        grand_total: 0,
        rounded_grand_total: 0,
        round_off: 0,
        enable_rounding: true,
        paid_amount: 0,
        due_amount: 0,
        payment_status: 'unpaid',
        refund_method: '',
        reason: '',
        notes: '',
        status: 'pending',
        items: [],
        original_sale: null
    });

    const [customers, setCustomers] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [loading, setLoading] = useState(false);
    const [loadingCustomers, setLoadingCustomers] = useState(false);
    const [selectedDate, setSelectedDate] = useState(dayjs());
    const [invoiceSearch, setInvoiceSearch] = useState('');
    const [searchingInvoice, setSearchingInvoice] = useState(false);
    const [saleSearchResults, setSaleSearchResults] = useState([]);
    const [showSaleSearch, setShowSaleSearch] = useState(false);
    const [selectedSale, setSelectedSale] = useState(null);
    const [customerError, setCustomerError] = useState('');

    // Refund method options
    const refundMethodOptions = [
        { value: 'cash', label: 'Cash Refund' },
        { value: 'bank_transfer', label: 'Bank Transfer' },
        { value: 'credit', label: 'Store Credit' },
        { value: 'card', label: 'Card Refund' },
        { value: 'cheque', label: 'Cheque' },
        { value: 'upi', label: 'UPI' }
    ];

    // Status options
    const statusOptions = [
        { value: 'pending', label: 'Pending' },
        { value: 'approved', label: 'Approved' },
        { value: 'received', label: 'Received' },
        { value: 'completed', label: 'Completed' }
    ];

    // Load customers
    const loadCustomers = useCallback(async (search = '') => {
        try {
            setLoadingCustomers(true);
            setCustomerError('');

            const params = { limit: 50 };
            if (search) {
                params.search = search;
            }

            const result = await customerActions.getCustomers(params);

            if (result.success) {
                if (result.data && result.data.length > 0) {
                    const customerList = result.data.map(c => ({
                        value: c.id,
                        label: `${c.name} ${c.phone ? `(${c.phone})` : ''}`,
                        phone: c.phone,
                        email: c.email,
                        address: c.address,
                        ...c
                    }));
                    setCustomers(customerList);
                } else {
                    setCustomers([]);
                }
            } else {
                setCustomerError('Failed to load customers');
            }
        } catch (error) {
            console.error('Error loading customers:', error);
            setCustomerError('Error loading customers');
        } finally {
            setLoadingCustomers(false);
        }
    }, []);

    // Load initial customers
    useEffect(() => {
        loadCustomers();
    }, [loadCustomers]);

    // Handle customer search
    const handleCustomerSearch = (inputValue) => {
        if (inputValue.length >= 2) {
            loadCustomers(inputValue);
        }
        return inputValue;
    };

    // Search invoices
    const handleInvoiceSearch = async () => {
        if (!invoiceSearch || invoiceSearch.length < 3) {
            Swal.fire('Info', 'Please enter at least 3 characters to search', 'info');
            return;
        }

        try {
            setSearchingInvoice(true);
            const result = await saleReturnActions.searchSalesByInvoice(invoiceSearch);

            if (result?.success && result.data && result.data.length > 0) {
                setSaleSearchResults(result.data);
                setShowSaleSearch(true);
            } else {
                setSaleSearchResults([]);
                setShowSaleSearch(false);
                Swal.fire('Info', 'No sales found with this invoice number', 'info');
            }
        } catch (error) {
            console.error('Error searching invoice:', error);
            Swal.fire('Error', 'Failed to search invoice', 'error');
        } finally {
            setSearchingInvoice(false);
        }
    };

    // Load sale for return
    const loadSaleForReturn = async (saleId) => {
        try {
            setLoading(true);
            const result = await saleReturnActions.getSaleForReturn(saleId);

            if (result.success) {
                const { sale, items } = result.data;

                setSelectedSale(sale);
                setFormData(prev => ({
                    ...prev,
                    sale_id: sale.id,
                    original_invoice_no: sale.invoice_no,
                    customer_id: sale.customer_id,
                    customer_name: sale.customer_name,
                    customer_phone: sale.customer_phone || '',
                    items: items.map(item => ({
                        ...item,
                        return_quantity: 0,
                        return_reason: '',
                        selected: false
                    }))
                }));

                // Set customer
                if (sale.customer_id) {
                    setSelectedCustomer({
                        value: sale.customer_id,
                        label: sale.customer_name,
                        phone: sale.customer_phone
                    });
                }

                setShowSaleSearch(false);
                setInvoiceSearch('');
            } else {
                Swal.fire('Error', result.message || 'Failed to load sale', 'error');
            }
        } catch (error) {
            console.error('Error loading sale:', error);
            Swal.fire('Error', 'Failed to load sale', 'error');
        } finally {
            setLoading(false);
        }
    };

    // Select sale from search
    const handleSelectSale = (sale) => {
        loadSaleForReturn(sale.id);
    };

    // Calculate totals with proper tax calculation
    const calculateTotals = useCallback((items, enableRounding = true) => {
        const returnItems = items.filter(item => item.return_quantity > 0);

        const no_of_items = returnItems.length;
        const total_qty = returnItems.reduce((sum, i) => sum + (i.return_quantity || 0), 0);

        const subtotal = returnItems.reduce((sum, i) =>
            sum + (i.unit_price * i.return_quantity), 0);

        // Calculate tax based on unit price and tax percentage
        const taxAmount = returnItems.reduce((sum, i) => {
            const taxPerItem = (i.unit_price * i.tax_percent) / 100;
            return sum + (taxPerItem * i.return_quantity);
        }, 0);

        const grandTotal = subtotal + taxAmount;

        // Calculate rounded values
        let roundedGrandTotal = grandTotal;
        let roundOff = 0;

        if (enableRounding) {
            roundedGrandTotal = roundAmount(grandTotal);
            roundOff = roundedGrandTotal - grandTotal;
        }

        return {
            no_of_items,
            total_qty,
            subtotal: parseFloat(subtotal.toFixed(2)),
            tax: parseFloat(taxAmount.toFixed(2)),
            grand_total: parseFloat(grandTotal.toFixed(2)),
            rounded_grand_total: parseFloat(roundedGrandTotal.toFixed(2)),
            round_off: parseFloat(roundOff.toFixed(2))
        };
    }, []);

    // Handle return quantity change
    const handleReturnQtyChange = (index, value) => {
        const qty = parseFloat(value) || 0;

        setFormData(prev => {
            const updatedItems = [...prev.items];
            const item = { ...updatedItems[index] };

            const validQty = Math.min(Math.max(0, qty), item.max_return_qty);

            updatedItems[index] = {
                ...item,
                return_quantity: validQty,
                selected: validQty > 0
            };

            const totals = calculateTotals(updatedItems, prev.enable_rounding);

            // Use the appropriate grand total based on rounding setting
            const currentGrandTotal = prev.enable_rounding ? totals.rounded_grand_total : totals.grand_total;
            const dueAmount = currentGrandTotal - prev.paid_amount;

            return {
                ...prev,
                items: updatedItems,
                ...totals,
                due_amount: dueAmount > 0 ? dueAmount : 0,
                payment_status: dueAmount <= 0 ? 'paid' : (prev.paid_amount > 0 ? 'partial' : 'unpaid')
            };
        });
    };

    // Handle select all items
    const handleSelectAll = (checked) => {
        setFormData(prev => {
            const updatedItems = prev.items.map(item => ({
                ...item,
                return_quantity: checked ? item.max_return_qty : 0,
                selected: checked
            }));

            const totals = calculateTotals(updatedItems, prev.enable_rounding);

            // Use the appropriate grand total based on rounding setting
            const currentGrandTotal = prev.enable_rounding ? totals.rounded_grand_total : totals.grand_total;
            const dueAmount = currentGrandTotal - prev.paid_amount;

            return {
                ...prev,
                items: updatedItems,
                ...totals,
                due_amount: dueAmount > 0 ? dueAmount : 0,
                payment_status: dueAmount <= 0 ? 'paid' : (prev.paid_amount > 0 ? 'partial' : 'unpaid')
            };
        });
    };

    // Handle return reason change
    const handleReasonChange = (index, value) => {
        setFormData(prev => {
            const updatedItems = [...prev.items];
            updatedItems[index] = {
                ...updatedItems[index],
                return_reason: value
            };
            return {
                ...prev,
                items: updatedItems
            };
        });
    };

    // Handle refund method change
    const handleRefundMethodChange = (option) => {
        setFormData(prev => ({
            ...prev,
            refund_method: option?.value || ''
        }));
    };

    // Handle status change
    const handleStatusChange = (option) => {
        setFormData(prev => ({ ...prev, status: option?.value || 'pending' }));
    };

    // Handle date change
    const handleDateChange = (date) => {
        if (date) {
            setSelectedDate(date);
            setFormData(prev => ({ ...prev, return_date: date.format('YYYY-MM-DD') }));
        }
    };

    // Handle paid amount change (refunded amount)
    const handlePaidAmountChange = (value) => {
        const paidAmount = parseFloat(value) || 0;
        const currentGrandTotal = formData.enable_rounding ? formData.rounded_grand_total : formData.grand_total;
        const validPaidAmount = Math.min(paidAmount, currentGrandTotal);
        const dueAmount = currentGrandTotal - validPaidAmount;

        let paymentStatus = 'unpaid';
        if (validPaidAmount >= currentGrandTotal) paymentStatus = 'paid';
        else if (validPaidAmount > 0) paymentStatus = 'partial';

        setFormData(prev => ({
            ...prev,
            paid_amount: validPaidAmount,
            due_amount: dueAmount,
            payment_status: paymentStatus
        }));
    };

    // Toggle rounding
    const handleToggleRounding = () => {
        setFormData(prev => {
            const newEnableRounding = !prev.enable_rounding;
            const totals = calculateTotals(prev.items, newEnableRounding);

            // Use the appropriate grand total based on new rounding setting
            const currentGrandTotal = newEnableRounding ? totals.rounded_grand_total : totals.grand_total;
            const dueAmount = currentGrandTotal - prev.paid_amount;

            return {
                ...prev,
                enable_rounding: newEnableRounding,
                ...totals,
                due_amount: dueAmount > 0 ? dueAmount : 0,
                payment_status: dueAmount <= 0 ? 'paid' : (prev.paid_amount > 0 ? 'partial' : 'unpaid')
            };
        });
    };

    // Handle customer change
    const handleCustomerChange = (option) => {
        setSelectedCustomer(option);
        setFormData(prev => ({
            ...prev,
            customer_id: option?.value || '',
            customer_name: option?.label || '',
            customer_phone: option?.phone || ''
        }));
    };

    // Validate form
    const validateForm = () => {
        if (!formData.sale_id) {
            Swal.fire('Error', 'Please select an original sale', 'error');
            return false;
        }

        const returnItems = formData.items.filter(item => item.return_quantity > 0);
        if (returnItems.length === 0) {
            Swal.fire('Error', 'Please select at least one item to return', 'error');
            return false;
        }

        return true;
    };

    // Handle submit
    // In AddSaleReturnPage.jsx, update the handleSubmit function

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) return;

        try {
            setLoading(true);

            const returnItems = formData.items.filter(item => item.return_quantity > 0);

            // Use the appropriate grand total based on rounding setting
            const finalGrandTotal = formData.enable_rounding ? formData.rounded_grand_total : formData.grand_total;

            const dataToSend = {
                sale_id: formData.sale_id,
                original_invoice_no: formData.original_invoice_no,
                customer_id: formData.customer_id,
                return_date: formData.return_date,
                status: formData.status,
                refund_method: formData.refund_method,
                reason: formData.reason,
                notes: formData.notes,
                paid_amount: formData.paid_amount,
                due_amount: formData.due_amount,
                payment_status: formData.payment_status,
                subtotal: formData.subtotal,
                tax: formData.tax,
                grand_total: finalGrandTotal,
                rounded_grand_total: formData.rounded_grand_total,
                round_off: formData.round_off,
                enable_rounding: formData.enable_rounding,
                refund_to_credit: formData.refund_method === 'credit',
                items: returnItems.map(item => {
                    // Calculate tax per item based on unit price and tax percentage
                    const taxPerItem = (item.unit_price * item.tax_percent) / 100;
                    const itemTax = taxPerItem * item.return_quantity;
                    const itemSubtotal = item.unit_price * item.return_quantity;
                    const itemTotal = itemSubtotal + itemTax;

                    return {
                        sale_item_id: item.id,
                        product_id: item.product_id,
                        product_name: item.product_name,
                        sku: item.sku,
                        quantity: item.return_quantity,
                        unit_price: item.unit_price,
                        tax_percent: item.tax_percent,
                        tax_amount: itemTax,
                        total_cost: itemTotal,
                        reason: item.return_reason || formData.reason
                    };
                })
            };

            // Log the data as a string to see if it's valid JSON
            const jsonString = JSON.stringify(dataToSend);
            console.log('JSON string being sent:', jsonString);
            console.log('Data object:', dataToSend);

            // Check if it's valid JSON
            try {
                JSON.parse(jsonString);
                console.log('JSON is valid');
            } catch (e) {
                console.error('JSON is invalid:', e);
            }

            const result = await saleReturnActions.createReturn(dataToSend);

            if (result.success) {
                await Swal.fire({
                    icon: 'success',
                    title: 'Success!',
                    text: 'Return created successfully',
                    timer: 2000,
                    showConfirmButton: false
                });
                navigate('/sales-returns');
            } else {
                Swal.fire('Error', result.message || 'Failed to create return', 'error');
            }
        } catch (error) {
            console.error('Error creating return:', error);
            Swal.fire('Error', 'Failed to create return', 'error');
        } finally {
            setLoading(false);
        }
    };

    // Handle cancel
    const handleCancel = () => {
        if (formData.items.some(item => item.return_quantity > 0) || formData.sale_id) {
            Swal.fire({
                title: 'Discard Changes?',
                text: 'You have unsaved changes. Are you sure?',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#d33',
                cancelButtonColor: '#3085d6',
                confirmButtonText: 'Yes, discard'
            }).then((result) => {
                if (result.isConfirmed) {
                    navigate('/sales-returns');
                }
            });
        } else {
            navigate('/sales-returns');
        }
    };

    // Format currency
    const formatCurrency = (amount) => {
        return `₹${parseFloat(amount || 0).toFixed(2)}`;
    };

    // Format date
    const formatDate = (date) => {
        return date ? dayjs(date).format('DD/MM/YYYY') : 'N/A';
    };

    // Get current grand total based on rounding setting
    const currentGrandTotal = formData.enable_rounding ? formData.rounded_grand_total : formData.grand_total;

    // Calculate total return items
    const returnItems = formData.items.filter(item => item.return_quantity > 0);
    const totalReturnItems = returnItems.length;
    const totalReturnQty = returnItems.reduce((sum, i) => sum + i.return_quantity, 0);

    return (
        <div className="page-wrapper">
            <div className="content">
                {/* Header */}
                <div className="page-header">
                    <div className="page-title">
                        <h4>Add Sales Return</h4>
                        <h6>Create new sales return and process refund</h6>
                    </div>
                    <ul className="table-top-head">
                        <li>
                            <button
                                className="btn btn-outline-secondary"
                                onClick={handleCancel}
                            >
                                <ArrowLeft size={16} className="me-2" />
                                Back to Returns
                            </button>
                        </li>
                    </ul>
                </div>

                {/* Form */}
                <div className="card">
                    <div className="card-body">
                        <form onSubmit={handleSubmit}>
                            {/* Reference and Date Row */}
                            <div className="row mb-3">
                                <div className="col-md-4">
                                    <label className="form-label fw-bold">Return Reference</label>
                                    <input
                                        type="text"
                                        className="form-control bg-light"
                                        value={formData.reference_no}
                                        readOnly
                                    />
                                </div>
                                <div className="col-md-4">
                                    <label className="form-label fw-bold">Return Date</label>
                                    <DatePicker
                                        value={selectedDate}
                                        onChange={handleDateChange}
                                        format="DD/MM/YYYY"
                                        className="form-control"
                                        style={{ width: '100%' }}
                                        allowClear={false}
                                    />
                                </div>
                                <div className="col-md-4">
                                    <label className="form-label fw-bold">Status</label>
                                    <Select
                                        options={statusOptions}
                                        value={statusOptions.find(opt => opt.value === formData.status)}
                                        onChange={handleStatusChange}
                                        placeholder="Select Status"
                                    />
                                </div>
                            </div>

                            {/* Original Sale Search */}
                            <div className="row mb-3">
                                <div className="col-12">
                                    <label className="form-label fw-bold">Original Invoice</label>
                                    <div className="d-flex gap-2">
                                        <div className="flex-grow-1">
                                            <div className="input-group">
                                                <span className="input-group-text bg-white">
                                                    <Search size={16} className="text-muted" />
                                                </span>
                                                <input
                                                    ref={searchInvoiceRef}
                                                    type="text"
                                                    className="form-control"
                                                    placeholder="Enter invoice number to search..."
                                                    value={invoiceSearch}
                                                    onChange={(e) => setInvoiceSearch(e.target.value)}
                                                    onKeyPress={(e) => e.key === 'Enter' && handleInvoiceSearch()}
                                                />
                                            </div>
                                        </div>
                                        <button
                                            type="button"
                                            className="btn btn-primary"
                                            onClick={handleInvoiceSearch}
                                            disabled={searchingInvoice}
                                        >
                                            {searchingInvoice ? (
                                                <span className="spinner-border spinner-border-sm" />
                                            ) : (
                                                'Search'
                                            )}
                                        </button>
                                    </div>

                                    {/* Sale Search Results */}
                                    {showSaleSearch && saleSearchResults.length > 0 && (
                                        <div className="mt-2 border rounded shadow-sm bg-white" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                                            {saleSearchResults.map(sale => (
                                                <div
                                                    key={sale.id}
                                                    className="p-3 border-bottom hover-bg-light cursor-pointer"
                                                    onClick={() => handleSelectSale(sale)}
                                                    style={{ cursor: 'pointer', transition: 'all 0.2s' }}
                                                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                                                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                                                >
                                                    <div className="d-flex justify-content-between">
                                                        <div>
                                                            <strong className="text-dark">{sale.invoice_no}</strong>
                                                            <br />
                                                            <span className="text-secondary">
                                                                {sale.customer_name} - {formatDate(sale.sale_date)}
                                                            </span>
                                                        </div>
                                                        <div className="text-end">
                                                            <span className="text-primary fw-bold">{formatCurrency(sale.grand_total)}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Selected Sale Info */}
                            {selectedSale && (
                                <div className="row mb-3">
                                    <div className="col-12">
                                        <div className="card bg-light">
                                            <div className="card-body">
                                                <div className="row">
                                                    <div className="col-md-3">
                                                        <p className="mb-1 text-muted">Invoice No</p>
                                                        <h6 className="fw-bold text-dark">{selectedSale.invoice_no}</h6>
                                                    </div>
                                                    <div className="col-md-3">
                                                        <p className="mb-1 text-muted">Sale Date</p>
                                                        <h6 className="text-dark">{formatDate(selectedSale.sale_date)}</h6>
                                                    </div>
                                                    <div className="col-md-3">
                                                        <p className="mb-1 text-muted">Customer</p>
                                                        <h6 className="text-dark">{selectedSale.customer_name}</h6>
                                                    </div>
                                                    <div className="col-md-3">
                                                        <p className="mb-1 text-muted">Sale Amount</p>
                                                        <h6 className="text-primary">{formatCurrency(selectedSale.grand_total)}</h6>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Customer Section */}
                            <div className="row mb-3">
                                <div className="col-md-5">
                                    <label className="form-label fw-bold">Customer Name</label>
                                    <div className="d-flex">
                                        <div style={{ flex: 1 }}>
                                            {loadingCustomers ? (
                                                <div className="d-flex align-items-center">
                                                    <div className="spinner-border spinner-border-sm text-primary me-2" />
                                                    <span>Loading...</span>
                                                </div>
                                            ) : (
                                                <Select
                                                    options={customers}
                                                    value={selectedCustomer}
                                                    onChange={handleCustomerChange}
                                                    onInputChange={handleCustomerSearch}
                                                    placeholder="Select customer"
                                                    isClearable
                                                    isLoading={loadingCustomers}
                                                />
                                            )}
                                        </div>
                                    </div>
                                    {customerError && (
                                        <small className="text-danger">{customerError}</small>
                                    )}
                                </div>
                                <div className="col-md-3">
                                    <label className="form-label fw-bold">Phone</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={formData.customer_phone}
                                        onChange={(e) => setFormData(prev => ({ ...prev, customer_phone: e.target.value }))}
                                        placeholder="Phone number"
                                    />
                                </div>
                                <div className="col-md-4">
                                    <label className="form-label fw-bold">Refund Method</label>
                                    <Select
                                        options={refundMethodOptions}
                                        value={refundMethodOptions.find(opt => opt.value === formData.refund_method)}
                                        onChange={handleRefundMethodChange}
                                        placeholder="Select refund method"
                                        isClearable
                                    />
                                </div>
                            </div>

                            {/* Items Table */}
                            {formData.items.length > 0 && (
                                <div className="mb-4">
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <h6 className="fw-bold">Return Items</h6>
                                        <div className="form-check">
                                            <input
                                                type="checkbox"
                                                className="form-check-input"
                                                id="selectAll"
                                                checked={formData.items.every(item => item.selected)}
                                                onChange={(e) => handleSelectAll(e.target.checked)}
                                            />
                                            <label className="form-check-label" htmlFor="selectAll">
                                                Select All Items
                                            </label>
                                        </div>
                                    </div>
                                    <div className="table-responsive border rounded">
                                        <table className="table table-bordered mb-0">
                                            <thead className="table-light">
                                                <tr>
                                                    <th>Product Code</th>
                                                    <th>Product Name</th>
                                                    <th className="text-end">Original Qty</th>
                                                    <th className="text-end">Return Qty</th>
                                                    <th className="text-end">Rate</th>
                                                    <th className="text-end">Tax %</th>
                                                    <th className="text-end">Return Amount</th>
                                                    <th>Reason</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {formData.items.map((item, index) => {
                                                    // Calculate tax per item based on unit price and tax percentage
                                                    const taxPerItem = (item.unit_price * item.tax_percent) / 100;
                                                    const itemTax = taxPerItem * item.return_quantity;
                                                    const itemSubtotal = item.unit_price * item.return_quantity;
                                                    const itemTotal = itemSubtotal + itemTax;

                                                    return (
                                                        <tr key={item.id} className={item.return_quantity > 0 ? 'table-primary' : ''}>
                                                            <td>
                                                                <input
                                                                    type="text"
                                                                    className="form-control form-control-sm bg-light"
                                                                    value={item.product_code || ''}
                                                                    readOnly
                                                                />
                                                            </td>
                                                            <td>
                                                                <strong>{item.product_name}</strong>
                                                            </td>
                                                            <td className="text-end">
                                                                {item.original_quantity}
                                                            </td>
                                                            <td>
                                                                <div className="d-flex align-items-center">
                                                                    <input
                                                                        type="number"
                                                                        className="form-control form-control-sm text-end"
                                                                        value={item.return_quantity}
                                                                        onChange={(e) => handleReturnQtyChange(index, e.target.value)}
                                                                        min="0"
                                                                        max={item.max_return_qty}
                                                                        step="0.1"
                                                                        style={{ width: '80px' }}
                                                                    />
                                                                    <span className="ms-1 text-muted small">
                                                                        / {item.max_return_qty}
                                                                    </span>
                                                                </div>
                                                            </td>
                                                            <td className="text-end">
                                                                {formatCurrency(item.unit_price)}
                                                            </td>
                                                            <td className="text-end">
                                                                {item.tax_percent}%
                                                            </td>
                                                            <td className="text-end fw-bold">
                                                                {item.return_quantity > 0 ? formatCurrency(itemTotal) : '-'}
                                                            </td>
                                                            <td>
                                                                <input
                                                                    type="text"
                                                                    className="form-control form-control-sm"
                                                                    value={item.return_reason || ''}
                                                                    onChange={(e) => handleReasonChange(index, e.target.value)}
                                                                    placeholder="Reason"
                                                                    disabled={item.return_quantity === 0}
                                                                />
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}

                            {/* Summary Section */}
                            {totalReturnItems > 0 && (
                                <>
                                    <div className="row mb-3">
                                        <div className="col-md-6">
                                            <div className="row">
                                                <div className="col-md-6 mb-2">
                                                    <div className="d-flex justify-content-between align-items-center pb-2 border-bottom">
                                                        <span className="fw-semibold">No of Items</span>
                                                        <span className="fw-bold text-primary">{totalReturnItems}</span>
                                                    </div>
                                                </div>
                                                <div className="col-md-6 mb-2">
                                                    <div className="d-flex justify-content-between align-items-center pb-2 border-bottom">
                                                        <span className="fw-semibold">Total Qty</span>
                                                        <span className="fw-bold text-info">{totalReturnQty}</span>
                                                    </div>
                                                </div>
                                                <div className="col-md-6 mb-2">
                                                    <div className="d-flex justify-content-between align-items-center pb-2 border-bottom">
                                                        <span className="fw-semibold">Subtotal</span>
                                                        <span className="fw-bold text-success">{formatCurrency(formData.subtotal)}</span>
                                                    </div>
                                                </div>
                                                <div className="col-md-6 mb-2">
                                                    <div className="d-flex justify-content-between align-items-center pb-2 border-bottom">
                                                        <span className="fw-semibold">Tax</span>
                                                        <span className="fw-bold text-warning">{formatCurrency(formData.tax)}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="col-md-6">
                                            <div className="bg-primary text-white p-3 rounded">
                                                <div className="d-flex justify-content-between align-items-center mb-2">
                                                    <h5 className="mb-0 text-white">Return Total</h5>
                                                    <div className="d-flex align-items-center">
                                                        <button
                                                            type="button"
                                                            className={`btn btn-sm ${formData.enable_rounding ? 'btn-light' : 'btn-outline-light'} me-2`}
                                                            onClick={handleToggleRounding}
                                                            title={formData.enable_rounding ? "Rounding Enabled" : "Rounding Disabled"}
                                                        >
                                                            <RotateCcw size={14} className="me-1" />
                                                            {formData.enable_rounding ? 'Rounded' : 'Actual'}
                                                        </button>
                                                        <h3 className="mb-0 text-white">
                                                            {formatCurrency(currentGrandTotal)}
                                                        </h3>
                                                    </div>
                                                </div>
                                                {formData.enable_rounding && formData.round_off !== 0 && (
                                                    <div className="d-flex justify-content-end">
                                                        <small className="text-white-50">
                                                            (Actual: {formatCurrency(formData.grand_total)} |
                                                            Round Off: {formData.round_off > 0 ? '+' : ''}{formatCurrency(formData.round_off)})
                                                        </small>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Refund Section */}
                                    <div className="row mb-3">
                                        <div className="col-md-4">
                                            <label className="form-label fw-bold">Refund Amount</label>
                                            <input
                                                type="number"
                                                className="form-control"
                                                value={formData.paid_amount}
                                                onChange={(e) => handlePaidAmountChange(e.target.value)}
                                                min="0"
                                                max={currentGrandTotal}
                                                step="0.01"
                                            />
                                            <small className="text-muted">
                                                Max refund: {formatCurrency(currentGrandTotal)}
                                            </small>
                                        </div>
                                        <div className="col-md-4">
                                            <label className="form-label fw-bold">Pending Refund</label>
                                            <div className="input-group">
                                                <input
                                                    type="text"
                                                    className={`form-control ${formData.due_amount > 0 ? 'text-danger' : 'text-success'} bg-light fw-bold`}
                                                    value={formatCurrency(formData.due_amount)}
                                                    readOnly
                                                />
                                                {formData.enable_rounding && (
                                                    <span className="input-group-text bg-light text-muted small">
                                                        Rounded
                                                    </span>
                                                )}
                                            </div>
                                            {formData.enable_rounding && formData.grand_total !== formData.rounded_grand_total && (
                                                <small className="text-muted">
                                                    Actual pending: {formatCurrency(formData.grand_total - formData.paid_amount)}
                                                </small>
                                            )}
                                        </div>
                                        <div className="col-md-4">
                                            <label className="form-label fw-bold">Refund Status</label>
                                            <input
                                                type="text"
                                                className="form-control bg-light text-capitalize"
                                                value={formData.payment_status}
                                                readOnly
                                            />
                                        </div>
                                    </div>
                                </>
                            )}

                            {/* Reason and Notes */}
                            <div className="row mb-3">
                                <div className="col-md-6">
                                    <label className="form-label fw-bold">Return Reason</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={formData.reason}
                                        onChange={(e) => setFormData(prev => ({ ...prev, reason: e.target.value }))}
                                        placeholder="Overall return reason"
                                    />
                                </div>
                                <div className="col-md-6">
                                    <label className="form-label fw-bold">Additional Notes</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={formData.notes}
                                        onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                                        placeholder="Enter additional notes"
                                    />
                                </div>
                            </div>

                            {/* Action Buttons */}
                            <div className="text-end">
                                <button
                                    type="button"
                                    className="btn btn-secondary me-2"
                                    onClick={handleCancel}
                                >
                                    <X size={16} className="me-2" />
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="btn btn-primary"
                                    disabled={loading || totalReturnItems === 0}
                                >
                                    {loading ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" />
                                            Creating...
                                        </>
                                    ) : (
                                        <>
                                            <Save size={16} className="me-2" />
                                            Create Return
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AddSaleReturnPage;